import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { Combobox } from '@zoopla-eng/zds-components/combobox';
import { PinMedium } from '@zoopla-eng/zds-components/icons';
import {
    ADDRESS_POSTCODE_SUGGESTION_QUERY,
    GEO_LOCATIONS_QUERY
} from '@zoopla/listing-search-queries';

type Option = {
    id: string;
    text: string;
};

type GeoSuggestion = {
    geoIdentifier: string;
    geoLabel: string;
    geoSubLabel?: string;
};

type SuggestedAddress = {
    address: string;
    urlPath: string;
};

export enum QueryType {
    GeoLocations = 'GEO_LOCATIONS',
    AddressPostcodes = 'ADDRESS_POSTCODE'
}

type Props = {
    label: string;
    queryType?: QueryType;
    placeholder?: string;
    errorText?: string;
    onSelectedItemChange: (value: Option | null) => void;
    onInputValueChange?: (value?: string) => void;
    initialSelectedItem?: Option | null;
};

export const LocationSearchInput: FC<React.PropsWithChildren<Props>> = ({
    label,
    queryType = QueryType.GeoLocations,
    placeholder,
    errorText,
    onInputValueChange,
    onSelectedItemChange,
    initialSelectedItem = null
}) => {
    const apolloClient = useApolloClient();
    const [selectedItem, setSelectedItem] = useState<Option | null>(
        initialSelectedItem
    );
    const abortSignalRef = useRef<AbortSignal | null>(null);

    useEffect(() => {
        const controller = new AbortController();
        abortSignalRef.current = controller.signal;
        return () => {
            controller.abort();
        };
    }, []);

    const handleSelectedItemChange = (option: Option | null) => {
        setSelectedItem(option);
        onSelectedItemChange(option);
    };

    const fetchGeoItems = useMemo(
        () => async (value: string | null) => {
            if (!value) return [];
            try {
                const { data } = await apolloClient.query({
                    query: GEO_LOCATIONS_QUERY,
                    variables: { locationPrefix: value },
                    context: {
                        fetchOptions: {
                            signal: abortSignalRef.current
                        }
                    }
                });

                return data?.geoSuggestion?.map(
                    ({
                        geoLabel,
                        geoSubLabel,
                        geoIdentifier
                    }: GeoSuggestion) => ({
                        id: geoIdentifier,
                        text: geoSubLabel
                            ? `${geoLabel}  ${geoSubLabel}`
                            : geoLabel
                    })
                );
            } catch (e) {
                // use previous state on error
                return (pendingState: Option[]) => pendingState;
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const fetchAddressItems = useMemo(
        () => async (value: string | null) => {
            if (!value) return [];
            try {
                const { data } = await apolloClient.query({
                    query: ADDRESS_POSTCODE_SUGGESTION_QUERY,
                    variables: { addressPartial: value.toLowerCase() },
                    context: {
                        fetchOptions: {
                            signal: abortSignalRef.current
                        }
                    }
                });

                const addressSuggestions =
                    data.getSuggestedAddresses?.suggestions;

                return addressSuggestions
                    ? addressSuggestions?.map(
                          ({ address, urlPath }: SuggestedAddress) => ({
                              id: urlPath || '',
                              text: address
                          })
                      )
                    : [];
            } catch (e) {
                // use previous state on error
                return (pendingState: Option[]) => pendingState;
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    return (
        <Combobox
            label={label}
            wait={300}
            placeholder={placeholder}
            errorText={errorText}
            selectedItem={selectedItem}
            initialItems={[]}
            withLeftIcon={false}
            listIcon={<PinMedium />}
            onInputValueChange={onInputValueChange}
            onSelectedItemChange={handleSelectedItemChange}
            fetchItems={
                queryType === QueryType.AddressPostcodes
                    ? fetchAddressItems
                    : fetchGeoItems
            }
        />
    );
};
